/** What the card scan sheet does with a tap (STORY-089 M3), apart from React so a test can
 * drive it: the camera, the round, the decode and the gateway.
 *
 * - **The round is fixed at the tap.** The item, attempt, options and clock skew are taken
 *   before the camera opens. If the open round changed while the camera was up (another staff
 *   device closed it, opened the next item or a re-vote), nothing from the photo is posted
 *   and the sheet says so. Otherwise the reads go to the round of the tap, whatever the live
 *   frame shows once the decode ends, and the engine judges them: inside the close's grace
 *   they count, after it the refusal is shown.
 * - **The round just closed stays on the sheet.** The report is built from the round last
 *   submitted (`submitted`), not the live frame's, so its counts, refusals and posts in flight
 *   remain until the first photo of the next round, labelled as the round just closed.
 * - **A photo older than a minute** is not read (`captureTime`).
 * - **The card rows are read first** (M4). `readRows` reads the course's card rows when the
 *   sheet opens, with the teacher's own token (never the lease), and until it has answered no
 *   photo is taken and no card joins: a card joined under the wrong label keeps it for the rest
 *   of the session, because the session store writes the label only at a device's first join.
 *   The read stops after `REQUEST_TIMEOUT_MS`, and when the sheet closes. A read that fails,
 *   times out or answers with something other than a list of rows joins nothing, and the sheet
 *   offers to read again. A 401, a 403, or the course route's own 404 (`denied`) says the list is
 *   not this account's to read; any other 404 (`missing`: a host without the route) says this
 *   lesson's host has no card list. Neither offers a retry.
 * - **Read again with each photo.** The sheet stays open across rounds, and a mapping saved on
 *   the laptop mid-lesson applies to cards not yet joined. So each photo reads the rows again
 *   beside its decode, started after `camera.photo()` (which needs the tap's gesture), and its
 *   first joins use those rows. If that read fails, it costs that photo only: nothing from it is
 *   posted, the sheet says why, the last good rows stay, and the next photo reads again.
 * - **The label.** Each card joins with the label `cardJoinFor` gives it: its seat, or its
 *   printed number while the course has no seat rows. In a named session of a course with seat
 *   rows a card with no seat is left out, and the report lists its number; in an anonymous
 *   session every card joins by its number (owner decisions, 2026-10-09).
 * - **Never two respondents with one label.** `setLive` hands the gateway the labels of the
 *   frame's respondents, and a card whose label is already someone's in the session is left out
 *   unless it is that someone (see `gateway.ts`).
 * - **Not while reconnecting.** A frame that is not live may be missing a join another sheet
 *   made, so its labels cannot be trusted: no photo is taken while the live view reconnects, and
 *   a photo taken just before is not posted (`reconnecting`).
 * - **An anonymous session reads no rows.** It ignores them (every card joins by its number), so
 *   it neither waits on the read nor fails closed when the host has no such route.
 * - **The controller outlives the sheet.** The live view keeps one per session; closing the sheet
 *   (`detach`) drops the photo and stops a decode, but the posts already queued still go, and a
 *   reopened sheet shows them. `dispose` is for the live view closing.
 * - **Only an upright photo is posted.** A photo whose orientation could not be applied
 *   (`pixels.ts`) would read every card a quarter turn off, so nothing from it is posted
 *   (`orientation`). */
import { captureTime, REQUEST_TIMEOUT_MS, summarise, type CardGateway, type GatewayView, type RoundItem, type RoundSummary } from "../cards/gateway";
import type { CardScan, Pixels, Point } from "../cards/decode";
import { asCardRows, cardJoinFor, type CardRow } from "../cards/mapping";
import type { CardCamera } from "../platform/cardCamera";
import { channelSupports } from "./channels";

export type ScanPhase = "idle" | "reading" | "shown" | "failed";
/** Why the last photo was not read or not posted: the round changed while the camera was up,
 * the photo is an old one, the rows' read beside it failed, its orientation is unknown, or the
 * live view lost its connection meanwhile. */
export type ScanNotice = "roundMoved" | "oldPhoto" | "rowsFailed" | "orientation" | "reconnecting" | null;
export type ScanBlock = "reconnecting" | "noRound" | "unsupported" | null;
/** The read of the course's card rows: under way, answered, failed (it may answer if read
 * again), denied (a 401, a 403 or the course route's 404: not this account's list), or missing
 * (any other 404: the host has no such route). Reading again will not help the last two. */
export type RowsState = "reading" | "ready" | "failed" | "denied" | "missing";

/** A decoded photo. `upright` is false when its orientation could not be applied. `show` draws
 * it on the sheet, `close` drops what is left of it; the controller calls `close` whether or not
 * it shows. */
export type ReadPhoto = { scan: CardScan; upright: boolean; show: () => void; close: () => void };

/** Whether the live view offers the scan: cards on, a camera, and an item a card can carry. */
export const scanOffered = (cardsOn: boolean, camera: CardCamera | null, item: RoundItem | null): boolean =>
  cardsOn && camera !== null && item !== null && channelSupports("card", item.snapshot);

/** The steps of a photo's read, apart from the DOM so a test can order them. `P` is the preview
 * the sheet draws (a canvas of `SHOWN_SIDE`). */
export type PhotoSteps<B, P> = {
  bitmap: (blob: Blob) => Promise<{ bitmap: B; upright: boolean }>;
  pixels: (bitmap: B) => Pixels & { data: Uint8ClampedArray };
  preview: (bitmap: B) => P;
  closeBitmap: (bitmap: B) => void;
  decode: (pixels: Pixels & { data: Uint8ClampedArray }, signal: AbortSignal) => Promise<CardScan>;
  show: (preview: P, decodedWidth: number, scan: CardScan) => void;
  dropPreview: (preview: P) => void;
};

/** Read a photo with the least held at once: the full-size bitmap gives the decoder's pixels and
 * a small preview, and is closed before the decode is awaited, so the decode's peak is the
 * pixel buffer and its own arrays, never the camera's bitmap beside them. */
export async function readPhotoWith<B, P>(blob: Blob, signal: AbortSignal, steps: PhotoSteps<B, P>): Promise<ReadPhoto> {
  const { bitmap, upright } = await steps.bitmap(blob);
  let open = true;
  let preview: P | null = null;
  try {
    const pixels = steps.pixels(bitmap);
    preview = steps.preview(bitmap);
    steps.closeBitmap(bitmap);
    open = false;
    const decodedWidth = pixels.width;
    const scan = await steps.decode(pixels, signal);
    const shown = preview;
    return { scan, upright, show: () => steps.show(shown, decodedWidth, scan), close: () => steps.dropPreview(shown) };
  } catch (e) {
    if (open) steps.closeBitmap(bitmap);
    if (preview !== null) steps.dropPreview(preview);
    throw e;
  }
}

/** The detail of the curriculum course route's own 404 (`reach.py`): the course is not this
 * account's. A 404 without it comes from a host that has no such route. */
const NO_SUCH_COURSE = "no such course";

export const roundKey = (item: RoundItem | null): string | null => (item ? `${item.item_id}:${item.attempt}` : null);

type Deps = {
  gateway: CardGateway;
  camera: CardCamera | null;
  read: (blob: Blob, signal: AbortSignal) => Promise<ReadPhoto>;
  /** The course's card rows, with the teacher's own token: `listCourseDevices(course, "card")`.
   * The signal aborts it on a timeout or when the sheet closes. */
  readRows: (signal: AbortSignal) => Promise<unknown>;
  /** The session takes no labels (join mode `anonymous`): every card joins by its number. */
  anonymous?: boolean;
  now?: () => number;
  /** Tests shorten it; the sheet uses `REQUEST_TIMEOUT_MS`. */
  timeoutMs?: number;
  onChange?: () => void;
};

export class CardScanController {
  phase: ScanPhase = "idle";
  notice: ScanNotice = null;
  /** The last photo's own counts: cards read, things not read, and cards seen more than once. */
  photo: { read: number; unread: number; duplicates: number } | null = null;
  /** The round the gateway's cards belong to: the item last submitted. */
  submitted: RoundItem | null = null;
  /** The card rows' read; no card joins until it is `ready`. */
  rows: RowsState = "reading";
  /** The course's card rows, once read. */
  cardRows: readonly CardRow[] = [];
  private rowsRead: Promise<void> | null = null;
  /** Aborts the reads of the rows under way when the sheet closes. */
  private readonly closing = new AbortController();
  /** An anonymous session: the seat rule does not apply. */
  readonly anonymous: boolean;
  private live: { item: RoundItem | null; skewMs: number } = { item: null, skewMs: 0 };
  /** The live view's connection: false while it reconnects or is offline. */
  connected = true;
  private readonly listeners = new Set<() => void>();
  private run = 0;
  private abort: AbortController | null = null;
  private disposed = false;
  private readonly deps: Deps;
  private readonly now: () => number;

  constructor(deps: Deps) {
    this.deps = deps;
    this.now = deps.now ?? (() => Date.now());
    this.anonymous = deps.anonymous ?? false;
  }

  /** The live frame's open round (null when none is open), clock skew, respondents' labels (the
   * staff frame's `labels`) and whether the live view is connected, on every render. */
  setLive(item: RoundItem | null, skewMs: number, labels?: Readonly<Record<string, string>>, connected = true) {
    this.live = { item, skewMs };
    this.connected = connected;
    this.deps.gateway.setRoomLabels(Object.values(labels ?? {}));
  }

  /** Be told of each change (the open sheet re-renders); returns the unsubscribe. */
  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  }

  /** The sheet closed: the photo it showed and a decode under way are dropped, and the next
   * opening starts from "take a photo". The gateway goes on: its queued posts still leave. */
  detach() {
    this.run++;
    this.abort?.abort();
    this.abort = null;
    this.phase = "idle";
    this.notice = null;
    this.photo = null;
  }

  /** The gateway's cards and stop. */
  view(): GatewayView {
    return this.deps.gateway.view();
  }

  dispose() {
    this.disposed = true;
    this.abort?.abort();
    this.closing.abort();
    this.deps.gateway.dispose();
  }

  private changed() {
    if (this.disposed) return;
    this.deps.onChange?.();
    for (const fn of this.listeners) fn();
  }

  /** Read the course's card rows: when the sheet opens, and again when the teacher asks after a
   * failure. Only on success do the cards get their labels and may a photo be taken. */
  readRows(): Promise<void> {
    if (this.rows === "ready" || this.disposed) return Promise.resolve();
    if (this.anonymous) {
      // the rows would be ignored: every card joins by its number
      this.useRows([]);
      this.rows = "ready";
      this.changed();
      return Promise.resolve();
    }
    if (this.rowsRead) return this.rowsRead;
    this.rows = "reading";
    this.changed();
    const run = (async () => {
      const got = await this.fetchRows(this.closing.signal);
      try {
        if (this.disposed) return;
        if (got.ok) this.useRows(got.rows);
        // never the error itself; the sheet says the list could not be read
        this.rows = got.ok ? "ready" : got.why;
      } finally {
        this.rowsRead = null;
        this.changed();
      }
    })();
    this.rowsRead = run;
    return run;
  }

  /** One read of the rows, bounded by the timeout and by `signal`, whether or not `readRows`
   * honours its signal. A reply that is not a list of rows is a failed read: a misrouted proxy's
   * page is never taken for an empty list. Never throws. */
  private async fetchRows(signal: AbortSignal): Promise<{ ok: true; rows: readonly CardRow[] } | { ok: false; why: "failed" | "denied" | "missing" }> {
    const stop = new AbortController();
    const onAbort = () => stop.abort();
    signal.addEventListener("abort", onAbort);
    const timer = setTimeout(() => stop.abort(), this.deps.timeoutMs ?? REQUEST_TIMEOUT_MS);
    try {
      if (signal.aborted) return { ok: false, why: "failed" };
      const stopped = new Promise<never>((_, fail) => stop.signal.addEventListener("abort", () => fail(new Error("timeout"))));
      const rows = asCardRows(await Promise.race([this.deps.readRows(stop.signal), stopped]));
      return rows ? { ok: true, rows } : { ok: false, why: "failed" };
    } catch (e) {
      const { status, detail } = (e ?? {}) as { status?: unknown; detail?: unknown };
      if (status === 401 || status === 403) return { ok: false, why: "denied" };
      // the course route's own 404 is this account's reach; any other is a host without the route
      if (status === 404) return { ok: false, why: detail === NO_SUCH_COURSE ? "denied" : "missing" };
      return { ok: false, why: "failed" };
    } finally {
      clearTimeout(timer);
      signal.removeEventListener("abort", onAbort);
      stop.abort();
    }
  }

  private useRows(rows: readonly CardRow[]) {
    this.cardRows = rows;
    const session = { anonymous: this.anonymous };
    this.deps.gateway.setLabels((card) => cardJoinFor(rows, card, session).label);
  }

  /** Called within the tap: the file input opens only from a user gesture. */
  async take(): Promise<void> {
    const camera = this.deps.camera;
    // no card joins before the card rows are read: the button is off until then
    if (!camera || this.disposed || this.rows !== "ready" || !this.connected) return;
    // the round, its options and the skew, fixed now, before the camera is up
    const { item, skewMs } = this.live;
    if (!item || !channelSupports("card", item.snapshot)) return;
    const round = roundKey(item);
    const attached = this.run;
    const blob = await camera.photo();
    // the sheet closed while the camera was up: the photo is not wanted
    if (!blob || this.disposed || attached !== this.run) return;
    const changedAt = this.now();
    if (roundKey(this.live.item) !== round) {
      this.notice = "roundMoved";
      this.changed();
      return;
    }
    const captureAt = captureTime((blob as Blob & { lastModified?: number }).lastModified, changedAt);
    if (captureAt === null) {
      this.notice = "oldPhoto";
      this.changed();
      return;
    }
    // a newer photo replaces one still being read
    const mine = ++this.run;
    this.abort?.abort();
    this.notice = null;
    this.phase = "reading";
    this.changed();
    const abort = new AbortController();
    this.abort = abort;
    // the rows again, beside the decode and after the camera: a mapping saved since the sheet
    // opened applies to this photo's first joins
    const fresh: ReturnType<CardScanController["fetchRows"]> = this.anonymous ? Promise.resolve({ ok: true, rows: [] }) : this.fetchRows(abort.signal);
    let got: ReadPhoto | null = null;
    try {
      got = await this.deps.read(blob, abort.signal);
      const rows = await fresh;
      if (mine !== this.run || this.disposed) return;
      got.show();
      // the canvas is the copy the sheet keeps: the full-size bitmap goes now, not once the
      // posts settle, so a sweep's second photo is never decoded beside the first one's
      const scan = got.scan;
      const shownUpright = got.upright;
      got.close();
      got = null;
      // distinct cards seen more than once, not the extra sightings: one card seen three times is
      // one card that appeared twice or more
      this.photo = { read: scan.reads.length, unread: scan.unread, duplicates: new Set(scan.duplicates.map((d) => d.card)).size };
      this.phase = "shown";
      if (!shownUpright) {
        // its letters would be read a quarter turn off: nothing from it is posted
        this.notice = "orientation";
        this.changed();
        return;
      }
      if (!this.connected) {
        // the frame's labels may be missing a join: nothing from it is posted until it is back
        this.notice = "reconnecting";
        this.changed();
        return;
      }
      if (!rows.ok) {
        // fail closed for this photo only: nothing from it is posted, the sheet says why, and
        // the last good rows stay for the next photo, which reads them again
        this.notice = "rowsFailed";
        this.changed();
        return;
      }
      this.useRows(rows.rows);
      // a gateway that stopped takes nothing: the round just closed and the stop's sentence stay
      if (this.deps.gateway.view().stopped) {
        this.changed();
        return;
      }
      this.submitted = item;
      // the round of the tap, not the one the frame shows now: the engine judges the timing
      const sent = this.deps.gateway.submit(scan.reads, { item, captureAt, skewMs });
      this.changed();
      await sent;
    } catch {
      // never the error itself: it could carry nothing useful and the photo is not logged
      if (mine === this.run && !this.disposed) {
        this.phase = "failed";
        this.changed();
      }
    } finally {
      got?.close();
    }
  }
}

/** What the report shows, from the controller, the gateway's view and the live frame. */
export function reportOf(
  ctl: Pick<CardScanController, "phase" | "notice" | "photo" | "submitted" | "cardRows" | "anonymous"> & { connected?: boolean },
  view: GatewayView,
  live: RoundItem | null,
): { keys: string[]; summary: RoundSummary | null; previous: boolean; blocked: ScanBlock; byNumber: number } {
  const sub = ctl.submitted;
  const mine = sub && view.round === roundKey(sub) ? sub : null;
  const summary = mine ? summarise(view, (mine.snapshot.options ?? []).length) : null;
  const blocked: ScanBlock =
    ctl.connected === false ? "reconnecting" : !live ? "noRound" : !channelSupports("card", live.snapshot) ? "unsupported" : null;
  return {
    keys: ((mine ?? live)?.snapshot.options ?? []).map((o) => o.key),
    summary,
    previous: summary !== null && view.round !== roundKey(live),
    blocked,
    // cards of this round that joined, and whose row names a student: they joined by their
    // numbers. An anonymous session keeps no label, so there is nothing to say.
    byNumber:
      summary && !ctl.anonymous ? [...view.cards.keys()].filter((n) => view.joined.has(n) && cardJoinFor(ctl.cardRows, n).by === "student").length : 0,
  };
}

/** The marks over the photo, in the decoder's pixels: an outline for each card seen, dashed
 * for one not read or a copy, and, in a named session, the card's printed number. Never its
 * letter: the sheet may be on a screen the class can see, and the letter counts are in the
 * report below. In an anonymous session no number either. */
export function marksFor(
  scan: CardScan,
  anonymous: boolean,
): { outlines: { corners: readonly Point[]; dashed: boolean }[]; labels: { x: number; y: number; side: number; text: string }[] } {
  const outlines = [
    ...scan.reads.map((r) => ({ corners: r.corners, dashed: false })),
    ...scan.unreadAt.map((q) => ({ corners: q, dashed: true })),
    ...scan.duplicates.map((r) => ({ corners: r.corners, dashed: true })),
  ];
  const labels = anonymous
    ? []
    : scan.reads.map((r) => ({
        x: r.corners.reduce((s, p) => s + p.x, 0) / 4,
        y: r.corners.reduce((s, p) => s + p.y, 0) / 4,
        side: Math.hypot(r.corners[1].x - r.corners[0].x, r.corners[1].y - r.corners[0].y),
        text: String(r.card),
      }));
  return { outlines, labels };
}
